'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Columns3, Check, RotateCcw } from 'lucide-react';
import { cn } from '../lib/utils';

export interface ColumnVisibilityItem {
  key: string;
  label: string;
  locked?: boolean;
}

export interface ColumnVisibilityDropdownProps {
  columns: ColumnVisibilityItem[];
  visibleKeys: Record<string, boolean>;
  onToggle: (key: string) => void;
  onReset?: () => void;
  isCustomised?: boolean;
  className?: string;
}

export function ColumnVisibilityDropdown({
  columns,
  visibleKeys,
  onToggle,
  onReset,
  isCustomised = false,
  className,
}: ColumnVisibilityDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  const visibleCount = columns.filter(
    (column) => column.locked || visibleKeys[column.key] !== false,
  ).length;

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5',
          'font-body text-caption font-medium text-text-body transition-interactive duration-200',
          'hover:bg-surface-soft hover:text-text-heading',
          isOpen && 'bg-surface-soft ring-1 ring-statura-green-accessible/20',
        )}
        title="Toggle columns"
      >
        <Columns3 className="h-3.5 w-3.5" />
        Columns
        {isCustomised && (
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-statura-green-accessible text-[9px] font-bold text-white">
            {visibleCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-1 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="font-body text-caption font-medium text-text-muted">
              {visibleCount} of {columns.length} columns visible
            </p>
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {columns.map((col) => {
              const locked = col.locked === true;
              const isVisible = locked || visibleKeys[col.key] !== false;

              return (
                <button
                  key={col.key}
                  type="button"
                  disabled={locked}
                  onClick={() => onToggle(col.key)}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-3 py-1.5 text-left font-body text-body-sm transition-interactive duration-200',
                    locked ? 'cursor-not-allowed opacity-80' : 'hover:bg-surface-soft',
                    !isVisible && 'text-text-subtle',
                  )}
                >
                  <span
                    className={cn(
                      'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-interactive duration-200',
                      isVisible
                        ? 'border-statura-green-accessible bg-statura-green-accessible text-white'
                        : 'border-slate-300 bg-white',
                    )}
                  >
                    {isVisible && <Check className="h-2.5 w-2.5" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate">{col.label}</span>
                    {locked ? (
                      <span className="block text-[10px] text-text-muted">Required</span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>

          {onReset && isCustomised && (
            <div className="border-t border-slate-100 px-3 py-2">
              <button
                type="button"
                onClick={() => {
                  onReset();
                  close();
                }}
                className="inline-flex items-center gap-1.5 font-body text-caption font-medium text-text-muted transition-interactive duration-200 hover:text-text-heading"
              >
                <RotateCcw className="h-3 w-3" />
                Reset columns
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
